import { ROUTE_DOCS_LANDS, ROUTE_DOCS_PROJECTS } from '@/common/constants';
import { Badge } from '@/components/ui/badge.tsx';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ILands } from '@/features/lands/models/ILands';
import { IProjects } from '@/features/projects/models/IProject';
import { cn } from '@/lib/utils';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams, useSearchParams } from 'react-router-dom';
import { Pagination } from '../pagination/Pagination';

interface SidebarProps {
  isLand?: boolean;
  className?: string;
  items?: ILands[] | IProjects[];
  isLoading?: boolean;
  totalPages: number;
  totalElements: number;
}

export const Sidebar: FC<SidebarProps> = ({
  isLand = true,
  items = [],
  isLoading,
  totalPages,
  totalElements,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { lang, id, status } = useParams();
  const safeItems = Array.isArray(items) ? items : [];
  return (
    <div
      className={`flex flex-col w-[440px] min-w-[440px] bg-white ${isLand ? 'h-[calc(100vh-50px)]' : 'h-[calc(100vh)]'}  overflow-hidden border-r`}
    >
      <ScrollArea className={cn('flex-1')}>
        <div className="relative">
          <div className="sticky top-0 left-0 bg-white z-[2] h-[73px] flex items-center py-2.5 px-6 border-b">
            <p className="text-lg leading-7 text-midnight-default font-medium">
              {isLand ? t('all.lands') : t('all.project')}
            </p>
          </div>
          {safeItems.length === 0 && !isLoading ? (
            <div className="text-center text-black text-xl py-4">
              {t('no.data')}
            </div>
          ) : (
            safeItems.map((item, i) => (
              <NavLink
                to={{
                  pathname: isLand
                    ? `/${lang}/${ROUTE_DOCS_LANDS}/${status || 'LAND'}/${item?.id}`
                    : `/${lang}/${ROUTE_DOCS_PROJECTS}/${item?.id}`,
                  search: searchParams.toString(),
                }}
                key={i}
                className={cn(
                  'py-3 px-6 border-b bg-white cursor-pointer  transition-all grid grid-cols-12 gap-[10px] relative  duration-300',
                  id === item?.id && 'bg-green-default/10',
                )}
              >
                {isLand ? null : (
                  <Badge className={cn('absolute top-1.5 right-3 text-xs ')}>
                    {t(`${item?.status_name}`)}
                  </Badge>
                )}
                <span
                  className={cn(
                    id === item?.id
                      ? 'bg-green-default  opacity-100'
                      : 'opacity-0',
                    'duration-300 absolute left-0 inset-y-0 h-full w-1',
                  )}
                />
                <div className="flex flex-col items-start col-span-5">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('lands')}
                  </p>
                  <p className="text-sm text-midnight-default">
                    {item?.name_uz}
                  </p>
                </div>
                <div className="flex flex-col items-start col-span-6">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('lot.number')}
                  </p>
                  <p className="text-sm text-dark-blue w-[200px] font-semibold line-clamp-1">
                    {item?.regnum}
                  </p>
                </div>
                {isLand ? (
                  <div className="flex flex-col items-start col-span-5">
                    <p className="text-[11px] font-medium text-gray-graphite">
                      {t('cadastral.number')}
                    </p>
                    <p className="text-sm text-midnight-default w-[160px] truncate font-semibold">
                      {item?.cad_number}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-start col-span-5">
                    <p className="text-[11px] font-medium text-gray-graphite">
                      {t('farmer')}
                    </p>
                    <p className="text-sm w-32 text-midnight-default truncate">
                      {item?.farmer_name}
                    </p>
                  </div>
                )}

                <div className="flex flex-col items-start col-span-4">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('area')}
                  </p>
                  <p className="text-sm text-midnight-default">
                    {item?.area} ga
                  </p>
                </div>
                <div className="flex flex-col items-start col-span-3">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('farmer.stir')}
                  </p>
                  <p className="text-sm text-midnight-default">
                    {item?.tax_number}
                  </p>
                </div>
              </NavLink>
            ))
          )}
        </div>
      </ScrollArea>
      <div className="bg-gray-100 h-[70px] border-t flex items-center px-4">
        {totalElements > 0 && (
          <Pagination totalPages={totalPages} totalElements={totalElements} />
        )}
      </div>
    </div>
  );
};
